import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import AnnouncementBanner from "../components/AnnouncementBanner";

const section = "mb-28";
const sectionTitle = "text-2xl font-semibold mb-10 text-muted-foreground";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
  viewport: { once: true },
};

const colors = [
  { name: "Primary Green", css: "bg-primary", hex: "#1f3d32", usage: "Brand color, headings, buttons, text" },
  { name: "Cream", css: "bg-background", hex: "#FDFBF7", usage: "Page backgrounds" },
  { name: "Lavender", css: "bg-secondary", hex: "hsl(265 40% 80%)", usage: "Secondary accents, tags" },
  { name: "Terracotta", css: "bg-accent", hex: "hsl(13 70% 63%)", usage: "Accent highlights, CTAs" },
  { name: "Muted", css: "bg-muted", hex: "hsl(60 10% 90%)", usage: "Subtle backgrounds, dividers" },
  { name: "Muted Foreground", css: "bg-muted-foreground", hex: "hsl(158 20% 40%)", usage: "Secondary text, captions" },
];

const Design = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="w-full pt-32 pb-16 overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 tablet:px-12 laptop:px-20">

          <motion.h1
            {...fadeIn}
            className="font-funnel text-5xl tablet:text-6xl font-bold mb-4"
          >
            Brand Guide
          </motion.h1>
          <motion.p {...fadeIn} className="text-muted-foreground text-lg mb-20 max-w-xl">
            The qwip design system — logo, typography, colour, components, and tone of voice.
          </motion.p>

          {/* ── Logo ── */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Logo</h2>
            <div className="grid tablet:grid-cols-2 gap-6">
              <div className="bg-background border border-border rounded-2xl p-10 flex items-center justify-center">
                <span className="font-funnel font-bold text-5xl text-foreground">qwip</span>
              </div>
              <div className="bg-primary rounded-2xl p-10 flex items-center justify-center">
                <span className="font-funnel font-bold text-5xl text-primary-foreground">qwip</span>
              </div>
            </div>
            <div className="grid tablet:grid-cols-2 gap-6 mt-6">
              <div className="bg-card border border-border rounded-2xl p-6 flex flex-col items-center gap-3">
                <img src="/email-logo-lightmode.png" alt="Email logo light" className="h-10 object-contain" />
                <span className="text-sm text-muted-foreground">Email — light mode</span>
              </div>
              <div className="bg-foreground rounded-2xl p-6 flex flex-col items-center gap-3">
                <img src="/email-logo-darkmode.png" alt="Email logo dark" className="h-10 object-contain" />
                <span className="text-sm text-primary-foreground/60">Email — dark mode</span>
              </div>
            </div>
          </motion.div>

          {/* ── Typography ── */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Typography</h2>

            <div className="space-y-6">
              {/* Funnel Display */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-funnel font-bold text-xl">Funnel Display</h3>
                  <a href="https://fonts.google.com/specimen/Funnel+Display" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground underline">Link to font ↗</a>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Headings & display. Weights 400, 700.</p>
                <p className="font-funnel text-4xl tablet:text-6xl font-bold leading-tight">work your way</p>
                <p className="font-funnel text-4xl tablet:text-6xl font-normal leading-tight text-muted-foreground">work your way</p>
              </div>

              {/* Cormorant */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-funnel font-bold text-xl">Cormorant Garamond</h3>
                  <a href="https://fonts.google.com/specimen/Cormorant+Garamond" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground underline">Link to font ↗</a>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Italic accent only. Weight 400. Used for words like "anywhere", "easier", "focus".</p>
                <p className="font-cormorant italic text-4xl tablet:text-6xl leading-tight">anywhere.</p>
              </div>

              {/* DM Sans */}
              <div className="bg-card border border-border rounded-2xl p-8">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <h3 className="font-funnel font-bold text-xl">DM Sans</h3>
                  <a href="https://fonts.google.com/specimen/DM+Sans" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground underline">Link to font ↗</a>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Body text & UI. Weights 400, 500, 600, 700.</p>
                <div className="space-y-2">
                  <p className="font-normal text-lg">Regular 400 — Private, soundproof pods in the places you already are.</p>
                  <p className="font-medium text-lg">Medium 500 — Bookable by the minute.</p>
                  <p className="font-semibold text-lg">Semibold 600 — Quiet, on demand.</p>
                  <p className="font-bold text-lg">Bold 700 — Become a Partner</p>
                </div>
              </div>
            </div>

            {/* Type pairing */}
            <div className="mt-10 bg-card border border-border rounded-2xl p-10">
              <p className="text-sm text-muted-foreground mb-6">Type pairing — hero lockup</p>
              <h1 className="font-funnel text-5xl tablet:text-7xl laptop:text-8xl font-bold leading-[0.9] tracking-tight">
                work your way,
                <br />
                <span className="font-cormorant italic font-normal">anywhere.</span>
              </h1>
              <p className="text-muted-foreground text-lg mt-4 max-w-xl">
                Private, soundproof pods in the places you already are. Bookable by the minute. Quiet, on demand.
              </p>
            </div>
          </motion.div>

          {/* ── Colour Palette ── */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Colour Palette</h2>
            <div className="grid tablet:grid-cols-2 laptop:grid-cols-3 gap-6">
              {colors.map((c) => (
                <div key={c.name} className="bg-card border border-border rounded-2xl p-6">
                  <div className={`w-full h-24 rounded-xl mb-4 ${c.css} border border-border`} />
                  <h3 className="text-lg font-semibold mb-1">{c.name}</h3>
                  <p className="text-sm font-mono text-muted-foreground mb-1">{c.hex}</p>
                  <p className="text-sm text-muted-foreground">{c.usage}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── Buttons ── */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Buttons</h2>
            <div className="bg-card border border-border rounded-2xl p-8 space-y-8">
              <div>
                <p className="text-sm text-muted-foreground mb-4">Default size</p>
                <div className="flex flex-wrap gap-4 items-center">
                  <Button variant="default">Primary</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="link">Link</Button>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-4">Large size</p>
                <div className="flex flex-wrap gap-4 items-center">
                  <Button variant="default" size="lg">Primary</Button>
                  <Button variant="outline" size="lg">Outline</Button>
                  <Button variant="secondary" size="lg">Secondary</Button>
                  <Button variant="ghost" size="lg">Ghost</Button>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-4">Pill style (used in navbar)</p>
                <a className="inline-block px-6 py-2.5 border border-foreground/20 rounded-full font-medium text-sm hover:bg-muted transition-all cursor-pointer">
                  Become a Partner
                </a>
              </div>
            </div>
          </motion.div>

          {/* ── Components ── */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Components</h2>

            <div className="space-y-6">
              {/* Announcement banner */}
              <div>
                <p className="text-sm text-muted-foreground mb-3">Announcement Banner</p>
                <div className="rounded-2xl overflow-hidden border border-border">
                  <AnnouncementBanner />
                </div>
              </div>

              {/* Navbar */}
              <div>
                <p className="text-sm text-muted-foreground mb-3">Navbar</p>
                <div className="rounded-2xl overflow-hidden border border-border bg-background px-6 py-6 flex items-center justify-between">
                  <span className="font-funnel font-bold text-3xl text-foreground">qwip</span>
                  <a className="px-6 py-2.5 border border-foreground/20 rounded-full font-medium text-sm hover:bg-muted transition-all cursor-pointer">
                    Become a Partner
                  </a>
                </div>
              </div>

              {/* Card */}
              <div>
                <p className="text-sm text-muted-foreground mb-3">Card</p>
                <div className="bg-card border border-border rounded-2xl p-8">
                  <h3 className="font-funnel font-bold text-xl mb-2">Card title</h3>
                  <p className="text-muted-foreground">This is a standard card using <code className="text-sm bg-muted px-1.5 py-0.5 rounded">bg-card</code> with <code className="text-sm bg-muted px-1.5 py-0.5 rounded">border-border</code> and <code className="text-sm bg-muted px-1.5 py-0.5 rounded">rounded-2xl</code>.</p>
                </div>
              </div>

              {/* Badge pills */}
              <div>
                <p className="text-sm text-muted-foreground mb-3">Badge Pills</p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">✓ Fully soundproof</span>
                  <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">⚡ Book from £0.15/min</span>
                  <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">📍 King's Cross</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── Tone of Voice ── */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Tone of Voice</h2>
            <div className="bg-card border border-border rounded-2xl p-8 space-y-6">
              <div>
                <h3 className="font-funnel font-bold text-lg mb-2">Calm confidence, lowercase-first</h3>
                <p className="text-muted-foreground">We write in lowercase where it feels natural. No shouting, no hype. The product speaks for itself.</p>
              </div>
              <div>
                <h3 className="font-funnel font-bold text-lg mb-2">Short, active, human</h3>
                <p className="text-muted-foreground">Short sentences. Active voice. Conversational but not sloppy. We explain complex things simply.</p>
              </div>
              <div>
                <h3 className="font-funnel font-bold text-lg mb-2">Examples from the site</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex gap-2"><span className="text-foreground">→</span> "work your way, <em className="font-cormorant">anywhere.</em>"</li>
                  <li className="flex gap-2"><span className="text-foreground">→</span> "Private, soundproof pods in the places you already are."</li>
                  <li className="flex gap-2"><span className="text-foreground">→</span> "Quiet, on demand."</li>
                  <li className="flex gap-2"><span className="text-foreground">→</span> "Bookable by the minute."</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* ── Design Tokens ── */}
          <motion.div {...fadeIn} className={section}>
            <h2 className={sectionTitle}>Design Tokens</h2>
            <div className="grid tablet:grid-cols-2 laptop:grid-cols-3 gap-6">
              <div className="bg-card rounded-2xl p-6 border border-border">
                <h4 className="font-semibold mb-4">Border Radius</h4>
                <div className="flex gap-4 items-end">
                  <div className="w-12 h-12 bg-primary rounded-sm" />
                  <div className="w-12 h-12 bg-primary rounded-md" />
                  <div className="w-12 h-12 bg-primary rounded-lg" />
                  <div className="w-12 h-12 bg-primary rounded-xl" />
                  <div className="w-12 h-12 bg-primary rounded-2xl" />
                  <div className="w-12 h-12 bg-primary rounded-full" />
                </div>
                <p className="text-sm text-muted-foreground mt-4">sm → md → lg → xl → 2xl → full</p>
              </div>
              <div className="bg-card rounded-2xl p-6 border border-border">
                <h4 className="font-semibold mb-4">Font Sizes</h4>
                <div className="space-y-2">
                  <p className="text-sm">text-sm (14px)</p>
                  <p className="text-base">text-base (16px)</p>
                  <p className="text-lg">text-lg (18px)</p>
                  <p className="text-xl">text-xl (20px)</p>
                  <p className="text-2xl">text-2xl (24px)</p>
                </div>
              </div>
              <div className="bg-card rounded-2xl p-6 border border-border">
                <h4 className="font-semibold mb-4">Spacing Scale</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 bg-accent rounded-sm" />
                    <span className="text-sm text-muted-foreground">4 — gap-1, p-1</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-accent rounded-sm" />
                    <span className="text-sm text-muted-foreground">6 — gap-1.5, p-1.5</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-accent rounded-sm" />
                    <span className="text-sm text-muted-foreground">8 — gap-2, p-2</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-accent rounded-sm" />
                    <span className="text-sm text-muted-foreground">12 — gap-3, p-3</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-4 bg-accent rounded-sm" />
                    <span className="text-sm text-muted-foreground">16 — gap-4, p-4</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Design;
